import { ADMIN_BASE, SELF_BASE } from './auth';

/** Request timeout in milliseconds */
const REQUEST_TIMEOUT_MS = 30_000;

/** Structured API error with HTTP status code */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    detail: string,
  ) {
    super(`${status} ${detail}`.trim());
    this.name = 'ApiError';
  }
}

/** Typed HTTP client for Keycloak REST endpoints */
export interface ApiClient {
  /** Send a GET request and parse the JSON response */
  get<T>(path: string): Promise<T>;
  /** Send a POST request and parse the JSON response */
  post<T>(path: string, body?: unknown): Promise<T>;
  /** Send a POST request and extract the resource ID from the Location header */
  postForId(path: string, body?: unknown): Promise<string>;
  /** Send a PUT request */
  put(path: string, body?: unknown): Promise<void>;
  /** Send a DELETE request with optional body */
  del(path: string, body?: unknown): Promise<void>;
}

/** Execute a fetch with a timeout via AbortController */
function fetchWithTimeout(url: string, init: RequestInit): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  return fetch(url, { ...init, signal: controller.signal })
    .finally(() => clearTimeout(timer));
}

/** Throw an ApiError if the response status is not 2xx */
async function assertOk(res: Response, label: string): Promise<void> {
  if (res.ok) return;
  const body = await res.text().catch(() => '');
  throw new ApiError(res.status, `${label}: ${body}`);
}

/**
 * Parse a JSON response, returning undefined when there is no body. Keycloak
 * answers creation POSTs (e.g. `POST /roles`) with `201 Created` and an empty
 * body, so keying only off `204` would make `res.json()` throw "Unexpected end
 * of JSON input". Reading the text first lets any empty body resolve cleanly.
 */
async function parseBody<T>(res: Response): Promise<T> {
  if (res.status === 204 || res.status === 205) return undefined as T;
  const text = await res.text();
  if (text.trim() === '') return undefined as T;
  return JSON.parse(text) as T;
}

/** Serialize a request body to JSON when present */
function buildInit(
  method: string,
  headers: Record<string, string>,
  body?: unknown,
): RequestInit {
  const init: RequestInit = { method, headers };
  if (body !== undefined) {
    init.body = JSON.stringify(body);
  }
  return init;
}

/** Create a typed API client bound to a base URL and bearer token */
export function createApiClient(baseUrl: string, token: string): ApiClient {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };

  async function get<T>(path: string): Promise<T> {
    const res = await fetchWithTimeout(`${baseUrl}${path}`, { headers });
    await assertOk(res, `GET ${path}`);
    return parseBody<T>(res);
  }

  async function post<T>(path: string, body?: unknown): Promise<T> {
    const res = await fetchWithTimeout(`${baseUrl}${path}`, buildInit('POST', headers, body));
    await assertOk(res, `POST ${path}`);
    return parseBody<T>(res);
  }

  async function postForId(path: string, body?: unknown): Promise<string> {
    const res = await fetchWithTimeout(`${baseUrl}${path}`, buildInit('POST', headers, body));
    await assertOk(res, `POST ${path}`);
    const location = res.headers.get('Location') ?? '';
    return location.split('/').pop() ?? '';
  }

  async function put(path: string, body?: unknown): Promise<void> {
    const res = await fetchWithTimeout(`${baseUrl}${path}`, buildInit('PUT', headers, body));
    await assertOk(res, `PUT ${path}`);
  }

  async function del(path: string, body?: unknown): Promise<void> {
    const res = await fetchWithTimeout(`${baseUrl}${path}`, buildInit('DELETE', headers, body));
    await assertOk(res, `DELETE ${path}`);
  }

  return { get, post, postForId, put, del };
}

/** Create an admin API client from a bearer token */
export function adminClient(token: string): ApiClient {
  return createApiClient(ADMIN_BASE, token);
}

/** Create a self-service API client from a bearer token */
export function selfClient(token: string): ApiClient {
  return createApiClient(SELF_BASE, token);
}
